// @flow
import * as React from "react";

import {
  Card,
  DownloadWidget,
  Section,
  UploadWidget,
  Separator,
  FlashMessageContext,
  type AddMessage
} from "buba-components";
import {
  USER_TYPE__BANK_ADVISOR,
  USER_TYPE__CUSTOMER,
  USER_TYPE__GUARANTEE_AGENT
} from "buba-components/src/utils/userType";
import { logError } from "buba-components/src/utils/loggers";
import { mapUnprocessableEntityError } from "buba-components/src/data/statusErrors";
import { allowedFileFormats } from "buba-components/src/utils/fileFormats";
import {
  uploadAndCheckFileForCustomer,
  uploadAndCheckFileForGuaranteeAgent
} from "../../api/actions";

import styles from "./Documents.scss";

type UploadsProps = {
  bankOfferId: number,
  bankOfferUploadedFiles: Array<any>,
  downloadAction: ({ id: number }) => Promise<any>,
  fileRequests: Array<any>,
  inquiryProcessId: number,
  inquiryProcessUploadedFiles: Array<any>,
  onError: number => void,
  refetchFiles: () => Promise<any>
};

type UploadWidgetProps = {
  id: number,
  requestId?: number,
  uploadAction: (conf: Object) => Promise<any>
};

type UploadWidgetForGuaranteeAgentProps = {
  id: number,
  requestId?: number
};
type UploadWidgetForCustomerProps = {
  id: number,
  requestId?: number
};

const Uploads = ({
  bankOfferId,
  bankOfferUploadedFiles = [],
  downloadAction,
  fileRequests,
  inquiryProcessId,
  inquiryProcessUploadedFiles = [],
  onError,
  refetchFiles
}: UploadsProps) => {
  const { addMessage } = React.useContext<{ addMessage: AddMessage }>(FlashMessageContext);
  const uploadWidgetProps = ({ id, requestId, uploadAction }: UploadWidgetProps = {}) => ({
    fileTypes: allowedFileFormats,
    initialStatus: "not_uploaded",
    name: "uploadForCustomer",
    title: "Dokument auswählen",
    selectedCallback: ({ file, markFailure, markSuccess, reset }) => {
      uploadAction({
        id,
        file,
        file_upload_request_id: requestId
      })
        .then(res => {
          const { status } = res;
          if (status === "rejected") {
            markFailure();
          } else {
            markSuccess();
            refetchFiles();
          }
          reset();
        })
        .catch(err => {
          const { response: { status = "", data: { errors = [] } = {} } = {} } = err;

          if (status === 422) {
            errors.forEach(e => markFailure(mapUnprocessableEntityError(e.type)));
          } else {
            markFailure();
            onError(status);
          }
          reset();
          logError(err);
        });
    }
  });

  const uploadWidgetForGuaranteeAgentProps = ({
    id,
    requestId
  }: UploadWidgetForGuaranteeAgentProps) =>
    uploadWidgetProps({
      id,
      requestId,
      uploadAction: conf => uploadAndCheckFileForGuaranteeAgent(conf, { addMessage })
    });

  const uploadWidgetForCustomerProps = ({ id, requestId }: UploadWidgetForCustomerProps) =>
    uploadWidgetProps({
      id,
      requestId,
      uploadAction: conf => uploadAndCheckFileForCustomer(conf, { addMessage })
    });

  const uploadedFilesForCustomer = bankOfferUploadedFiles.filter(
    ({ hiddenFor }) => hiddenFor !== USER_TYPE__CUSTOMER
  );
  const uploadedFilesForGuaranteeAgent = inquiryProcessUploadedFiles.filter(
    ({ hiddenFor }) => hiddenFor !== USER_TYPE__GUARANTEE_AGENT
  );
  const requestedFromBankAdvisor = fileRequests.filter(
    ({ requestedFrom }) => requestedFrom === USER_TYPE__BANK_ADVISOR
  );

  return (
    <Section title="Upload-Bereich" numbered reducedMargin>
      <Card>
        {/* Documents for Client */}
        <header>Unterlagen für den Kunden</header>
        <div className={styles.widgetContainer}>
          <UploadWidget
            {...uploadWidgetForCustomerProps({ id: bankOfferId })}
            name="uploadForCustomer"
          />
          {uploadedFilesForCustomer.map(file => (
            <DownloadWidget
              uploadDate={file.createdAt}
              key={file.id}
              id={Number(file.id)}
              title={file.description || file.filename}
              status={file.downloadedByCustomerAt ? "downloaded" : "uploaded"}
              downloadAction={downloadAction}
              filename={file.filename}
            />
          ))}
        </div>

        <Separator />

        {/* Documents for BUBA (Guarantee agent) */}
        <header>Unterlagen für die Bürgschaftsbank</header>
        <div className={styles.widgetContainer}>
          <UploadWidget
            {...uploadWidgetForGuaranteeAgentProps({ id: inquiryProcessId })}
            name="uploadForGuaranteeAgent"
          />
          {requestedFromBankAdvisor.map(file => (
            <UploadWidget
              {...uploadWidgetForGuaranteeAgentProps({
                id: inquiryProcessId,
                requestId: Number(file.id)
              })}
              title={file.description}
              key={file.id}
              name="uploadForGuaranteeAgent"
            />
          ))}
          {uploadedFilesForGuaranteeAgent.map(file => (
            <DownloadWidget
              uploadDate={file.createdAt}
              key={file.id}
              id={Number(file.id)}
              title={file.description || file.filename}
              status={file.downloadedByGuaranteeAgentAt ? "downloaded" : "uploaded"}
              downloadAction={downloadAction}
              filename={file.filename}
            />
          ))}
        </div>
      </Card>
    </Section>
  );
};

export default Uploads;
